import { z } from 'zod'
import {
  DEFAULT_BLACKLISTED_EVENTS,
  DEFAULT_BLACKLISTED_EXPLOSIONS,
  DEFAULT_BLACKLISTED_OBJECTS,
  DEFAULT_BLACKLISTED_PEDS,
  DEFAULT_BLACKLISTED_PTFX,
  DEFAULT_BLACKLISTED_STRINGS,
  DEFAULT_BLACKLISTED_VEHICLES,
  DEFAULT_BLACKLISTED_WEAPONS,
  DEFAULT_HONEYPOT_CLIENT_COMMANDS,
  DEFAULT_HONEYPOT_CLIENT_EVENTS,
  DEFAULT_HONEYPOT_CLIENT_EXPORTS,
  DEFAULT_HONEYPOT_SERVER_COMMANDS,
  DEFAULT_HONEYPOT_SERVER_EVENTS,
  DEFAULT_HONEYPOT_SERVER_EXPORTS,
  CONFIG_SCHEMA_VERSION,
  FRAMEWORKS,
} from './constants.js'
import { ACTIONS, DETECTION_KEYS, DETECTION_META, type DetectionKey } from './detections.js'
import { MODULE_OPTION_SHAPES } from './module-options.js'

/**
 * SPEC.md §4.2 — the server configuration document.
 *
 * One `ServerConfig` per server, versioned in `server_configs` and pushed to the
 * agent by version. Every leaf has a default, so `ServerConfigSchema.parse({})`
 * yields a complete, valid configuration: that is how a new server is seeded and
 * how a partial import is completed.
 *
 * `toClientConfig()` is the security-critical part: it produces the strict
 * subset broadcast to players. Anything a cheater must not learn — server
 * honeypot names, bypass ACEs, connect policy, resource whitelists — must never
 * appear in its output.
 */

const ActionSchema = z.enum(ACTIONS)

/** Fields every module carries, independent of its own options. */
function moduleSchema<K extends DetectionKey>(key: K) {
  const defaults = DETECTION_META[key].defaults
  return z
    .object({
      enabled: z.boolean().default(defaults.enabled),
      action: ActionSchema.default(defaults.action),
      strikes: z.number().int().min(1).max(50).default(defaults.strikes),
      windowMs: z.number().int().min(1000).max(600_000).default(defaults.windowMs),
      screenshot: z.boolean().default(defaults.screenshot),
      /** 0 = permanent. Only meaningful when `action` is `ban`. */
      banDurationSec: z.number().int().min(0).max(31_536_000).default(0),
      // `{} as never` only silences the generic: every option in the shape has
      // its own default, so an empty object parses into the full option set.
      options: z.object(MODULE_OPTION_SHAPES[key]).prefault({} as never),
    })
    .prefault({})
}

type ModulesShape = { [K in DetectionKey]: ReturnType<typeof moduleSchema<K>> }

// Built from DETECTION_KEYS rather than written out 65 times. The `ModulesShape`
// annotation makes a missing or misspelled key a compile error, so this is as
// safe as a literal while staying in sync with the frozen key list.
const modulesShape = Object.fromEntries(DETECTION_KEYS.map((key) => [key, moduleSchema(key)])) as ModulesShape

export const ModulesSchema = z.object(modulesShape)
export type Modules = z.infer<typeof ModulesSchema>

export const ServerConfigSchema = z.object({
  schemaVersion: z.literal(CONFIG_SCHEMA_VERSION).default(CONFIG_SCHEMA_VERSION),

  general: z
    .object({
      framework: z.enum(FRAMEWORKS).default('standalone'),
      /** Master switch: every action is forced down to `log` (SPEC.md §1.2). */
      logOnly: z.boolean().default(false),
      banMessage: z
        .string()
        .max(300)
        .default('SentinelAC: Bu sunucudan yasaklandınız. Kod: {banCode} · Sebep: {reason} · İtiraz: {appealUrl}'),
      kickMessage: z.string().max(300).default('SentinelAC: {reason}'),
      appealUrl: z.union([z.url(), z.literal('')]).default(''),
      /** Grace after `playerSpawned`, during which no client module reports. */
      spawnGraceMs: z.number().int().min(5000).max(120_000).default(20_000),
      frameworkGraceMs: z.number().int().min(0).max(60_000).default(12_000),
      readySettleMs: z.number().int().min(0).max(60_000).default(18_000),
      clientHeartbeatIntervalMs: z.number().int().min(10_000).max(60_000).default(20_000),
      clientHeartbeatMissLimit: z.number().int().min(2).max(10).default(3),
      reportRateLimit: z
        .object({
          max: z.number().int().min(1).max(100).default(6),
          windowMs: z.number().int().min(1000).max(120_000).default(10_000),
        })
        .prefault({}),
      txAdminAdminsBypass: z.boolean().default(true),
      mirrorTxAdminBans: z.boolean().default(false),
      debug: z.boolean().default(false),
    })
    .prefault({}),

  connect: z
    .object({
      /** What to do when the cloud is unreachable during `playerConnecting`. */
      offlinePolicy: z.enum(['cache', 'allow', 'deny']).default('cache'),
      timeoutMs: z.number().int().min(2000).max(15_000).default(6000),
      requiredIdentifiers: z.array(z.enum(['license', 'steam', 'discord', 'fivem'])).max(4).default(['license']),
      nameBlacklistPatterns: z.array(z.string().max(100)).max(50).default([]),
      /** Single source of truth; mirrored to `servers.global_bans_mode` for SQL filtering. */
      globalBans: z.enum(['block', 'flag', 'off']).default('block'),
      banByIp: z.boolean().default(false),
      minTrustScore: z.number().int().min(0).max(100).default(0),
      maxSessionsPerIp: z.number().int().min(1).max(32).default(3),
      /** Sends the joining IP to a third party; the panel shows a KVKK/GDPR notice. */
      vpnCheck: z.boolean().default(false),
      deferralMessage: z.string().max(120).default('SentinelAC kimlik doğrulaması yapılıyor...'),
      messages: z
        .object({
          banned: z.string().max(300).default('SentinelAC: Bu sunucudan yasaklandınız. Kod: {banCode} · Sebep: {reason}'),
          globalBanned: z.string().max(300).default('SentinelAC: Küresel yasak listesindesiniz. Kod: {banCode}'),
          unreachable: z
            .string()
            .max(300)
            .default('SentinelAC: Doğrulama sunucusuna ulaşılamıyor, lütfen tekrar deneyin.'),
          missingIdentifiers: z.string().max(300).default('SentinelAC: Gerekli kimlikler eksik: {list}'),
          lowTrust: z.string().max(300).default('SentinelAC: Bu sunucuya katılma izniniz yok.'),
        })
        .prefault({}),
    })
    .prefault({}),

  bypass: z
    .object({
      /** ACE granting immunity from every module. */
      staffAce: z.string().max(64).default('sentinel.bypass'),
      /** ACE allowing spectate/freecam/noclip and the `/sac*` commands. */
      adminAce: z.string().max(64).default('sentinel.admin'),
      staffIdentifiers: z.array(z.string().max(120)).max(200).default([]),
    })
    .prefault({}),

  blacklists: z
    .object({
      weapons: z.array(z.string().max(64)).max(500).default([...DEFAULT_BLACKLISTED_WEAPONS]),
      peds: z.array(z.string().max(64)).max(500).default([...DEFAULT_BLACKLISTED_PEDS]),
      vehicles: z.array(z.string().max(64)).max(500).default([...DEFAULT_BLACKLISTED_VEHICLES]),
      objects: z.array(z.string().max(64)).max(500).default([...DEFAULT_BLACKLISTED_OBJECTS]),
      explosionTypes: z.array(z.number().int().min(0).max(255)).max(80).default([...DEFAULT_BLACKLISTED_EXPLOSIONS]),
      ptfx: z.array(z.string().max(64)).max(200).default([...DEFAULT_BLACKLISTED_PTFX]),
      events: z.array(z.string().max(120)).max(300).default([...DEFAULT_BLACKLISTED_EVENTS]),
      eventStrings: z.array(z.string().max(120)).max(300).default([...DEFAULT_BLACKLISTED_STRINGS]),
      chatStrings: z.array(z.string().max(120)).max(300).default([...DEFAULT_BLACKLISTED_STRINGS]),
    })
    .prefault({}),

  whitelists: z
    .object({
      /** Resources allowed to spawn entities and to call the Ban/Kick exports. */
      resources: z.array(z.string().max(64)).max(300).default([]),
      entityScripts: z.array(z.string().max(64)).max(300).default([]),
      models: z.array(z.string().max(64)).max(500).default([]),
      blipSprites: z.array(z.number().int().min(0).max(1000)).max(200).default([]),
      nightVisionResources: z.array(z.string().max(64)).max(100).default([]),
      textures: z.array(z.string().max(64)).max(200).default([]),
    })
    .prefault({}),

  honeypots: z
    .object({
      /** NEVER sent to clients: the client bundle contains no stub for these. */
      serverEvents: z.array(z.string().max(120)).max(100).default([...DEFAULT_HONEYPOT_SERVER_EVENTS]),
      clientEvents: z.array(z.string().max(120)).max(100).default([...DEFAULT_HONEYPOT_CLIENT_EVENTS]),
      clientExports: z.array(z.string().max(64)).max(100).default([...DEFAULT_HONEYPOT_CLIENT_EXPORTS]),
      clientCommands: z.array(z.string().max(64)).max(100).default([...DEFAULT_HONEYPOT_CLIENT_COMMANDS]),
      serverExports: z.array(z.string().max(64)).max(100).default([...DEFAULT_HONEYPOT_SERVER_EXPORTS]),
      serverCommands: z.array(z.string().max(64)).max(100).default([...DEFAULT_HONEYPOT_SERVER_COMMANDS]),
    })
    .prefault({}),

  screenshots: z
    .object({
      onBan: z.boolean().default(true),
      onSeverity: z.enum(['off', 'high', 'critical']).default('high'),
      encoding: z.enum(['jpg', 'png', 'webp']).default('jpg'),
      quality: z.number().min(0.1).max(1).default(0.7),
      perPlayerCooldownMs: z.number().int().min(1000).max(600_000).default(30_000),
      perServerPerMinute: z.number().int().min(1).max(120).default(20),
    })
    .prefault({}),

  safeEvents: z
    .object({
      enabled: z.boolean().default(true),
      /** Escalates an invalid token from `log` to `ban`. */
      strict: z.boolean().default(false),
      tokenTtlMs: z.number().int().min(10_000).max(600_000).default(120_000),
    })
    .prefault({}),

  /**
   * Cloud-authored rules over inbound net events — the feature FiveGuard ships
   * 66 of by default and the one server owners actually live in.
   *
   * A rule is deliberately not a detection module: modules are a fixed catalog
   * with a documented meaning each, while these are per-server and are written
   * by the owner against their own resources. They all report through the one
   * `event_rule` module, which carries the enabled flag and the strike window;
   * the rule carries the specific action, so "log this one, kick on that one"
   * needs no new module.
   *
   * `mode` covers both shapes a rule takes in practice:
   *   * `match` fires the first time an event matches its conditions — a
   *     blocklist ("nobody may trigger this at all").
   *   * `rate` fires when one player exceeds `maxPerWindow` inside
   *     `windowMs` — a limiter ("this pays out, so once a minute is plenty").
   */
  /**
   * The live map. Server-side only: the agent reads every online player's
   * coordinates with `GetEntityCoords(GetPlayerPed(src))` and posts them, so
   * none of this reaches a game client and nothing here is in
   * `toClientConfig()`.
   *
   * Nothing is stored. `enabled = false` stops the traffic rather than hiding
   * the page, because a feed nobody watches is still bandwidth and still a
   * stream of where named people are standing.
   */
  liveMap: z
    .object({
      enabled: z.boolean().default(true),
      /** How often the agent posts the whole roster. */
      intervalMs: z.number().int().min(1000).max(60_000).default(3000),
      /**
       * An optional map image drawn under the player dots.
       *
       * The panel ships a drawn vector map of San Andreas, which is what the
       * dots land on by default. This field exists because the artwork most
       * FiveM tools use is Rockstar's, and shipping that inside a product we
       * sell is not ours to do — an operator who has a tile set they may use
       * points at it here instead.
       *
       * Empty means "use the drawn map". Only https is accepted: the panel is
       * served over TLS and a mixed-content image would simply not load.
       */
      imageUrl: z
        .union([z.literal(''), z.url().max(500).refine((value) => value.startsWith('https://'), 'must be https')])
        .default(''),
      /**
       * The world rectangle the image covers, so a tile set exported with
       * different bounds still lines up with the dots. The defaults are the
       * playable extent the drawn map uses.
       */
      imageBounds: z
        .object({
          minX: z.number().default(-4000),
          maxX: z.number().default(4500),
          minY: z.number().default(-4000),
          maxY: z.number().default(8000),
        })
        .prefault({}),
    })
    .prefault({}),

  eventRules: z
    .array(
      z
        .object({
          id: z.string().min(1).max(40),
          name: z.string().min(1).max(80),
          enabled: z.boolean().default(true),
          /** Net event names this rule watches. */
          events: z.array(z.string().min(1).max(150)).min(1).max(32),
          mode: z.enum(['rate', 'match']).default('rate'),
          maxPerWindow: z.number().int().min(1).max(1000).default(10),
          windowMs: z.number().int().min(1000).max(600_000).default(10_000),
          /**
           * All conditions must hold for the event to count. An empty list
           * means "every occurrence counts", which is what a plain limiter is.
           * `arg` is 1-based over the event's arguments as the server sees them.
           */
          conditions: z
            .array(
              z.object({
                arg: z.number().int().min(1).max(8),
                op: z.enum(['equals', 'notEquals', 'contains', 'gt', 'lt', 'isType', 'missing']),
                value: z.string().max(200).default(''),
              }),
            )
            .max(8)
            .default([]),
          action: ActionSchema.default('log'),
          /** Cancel the event as well as reporting it. */
          cancel: z.boolean().default(true),
        })
        .strict(),
    )
    .max(200)
    .default([]),

  modules: ModulesSchema.prefault({}),
})

export type ServerConfig = z.infer<typeof ServerConfigSchema>

/** The factory configuration a brand new server starts from. */
export const DEFAULT_SERVER_CONFIG: ServerConfig = ServerConfigSchema.parse({})

/**
 * The exact subset broadcast to every player (SPEC.md §4.2).
 *
 * Client modules only. No `connect`, no `bypass`, no server honeypot names, no
 * resource or entity-script whitelists: a cheater reading this must learn
 * nothing that helps them evade a server-side check.
 */
export const ClientConfigSchema = z.object({
  version: z.number().int(),
  general: z.object({
    framework: z.enum(FRAMEWORKS),
    spawnGraceMs: z.number().int(),
    frameworkGraceMs: z.number().int(),
    readySettleMs: z.number().int(),
    clientHeartbeatIntervalMs: z.number().int(),
    reportRateLimit: z.object({ max: z.number().int(), windowMs: z.number().int() }),
    debug: z.boolean(),
  }),
  modules: z.record(z.string(), z.unknown()),
  blacklists: z.object({
    weapons: z.array(z.string()),
    peds: z.array(z.string()),
  }),
  whitelists: z.object({
    blipSprites: z.array(z.number().int()),
    nightVisionResources: z.array(z.string()),
    textures: z.array(z.string()),
  }),
  honeypots: z.object({
    clientEvents: z.array(z.string()),
    clientExports: z.array(z.string()),
    clientCommands: z.array(z.string()),
  }),
  screenshots: z.object({
    encoding: z.enum(['jpg', 'png', 'webp']),
    quality: z.number(),
  }),
  safeEvents: z.object({ enabled: z.boolean(), tokenTtlMs: z.number().int() }),
})

export type ClientConfig = z.infer<typeof ClientConfigSchema>

/** Keys of `ServerConfig` that must never reach a game client. */
export const CLIENT_CONFIG_FORBIDDEN_PATHS = [
  'connect',
  'bypass',
  'honeypots.serverEvents',
  'honeypots.serverExports',
  'honeypots.serverCommands',
  'whitelists.resources',
  'whitelists.entityScripts',
] as const

export function toClientConfig(cfg: ServerConfig, version: number): ClientConfig {
  const modules: Record<string, unknown> = {}
  for (const key of DETECTION_KEYS) {
    if (DETECTION_META[key].side !== 'client') continue
    const module = cfg.modules[key]
    modules[key] = {
      enabled: module.enabled,
      // The client never learns whether a trip kicks or bans: it only reports.
      strikes: module.strikes,
      windowMs: module.windowMs,
      options: module.options,
    }
  }

  return {
    version,
    general: {
      framework: cfg.general.framework,
      spawnGraceMs: cfg.general.spawnGraceMs,
      frameworkGraceMs: cfg.general.frameworkGraceMs,
      readySettleMs: cfg.general.readySettleMs,
      clientHeartbeatIntervalMs: cfg.general.clientHeartbeatIntervalMs,
      reportRateLimit: cfg.general.reportRateLimit,
      debug: cfg.general.debug,
    },
    modules,
    blacklists: { weapons: cfg.blacklists.weapons, peds: cfg.blacklists.peds },
    whitelists: {
      blipSprites: cfg.whitelists.blipSprites,
      nightVisionResources: cfg.whitelists.nightVisionResources,
      textures: cfg.whitelists.textures,
    },
    honeypots: {
      clientEvents: cfg.honeypots.clientEvents,
      clientExports: cfg.honeypots.clientExports,
      clientCommands: cfg.honeypots.clientCommands,
    },
    screenshots: { encoding: cfg.screenshots.encoding, quality: cfg.screenshots.quality },
    safeEvents: { enabled: cfg.safeEvents.enabled, tokenTtlMs: cfg.safeEvents.tokenTtlMs },
  }
}

/** Stable JSON with sorted keys, so a checksum depends on content and not on key order. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`
}

/**
 * Brings a stored document up to the current schema version.
 *
 * Unknown keys are dropped rather than rejected, so removing a module in a later
 * release cannot lock an owner out of their own config. A document from a NEWER
 * schema version is refused, because silently downgrading would discard settings.
 */
export function migrateConfig(raw: unknown): ServerConfig {
  if (raw === null || typeof raw !== 'object') return { ...DEFAULT_SERVER_CONFIG }

  const document = raw as Record<string, unknown>
  const version = typeof document['schemaVersion'] === 'number' ? (document['schemaVersion'] as number) : 1

  if (version > CONFIG_SCHEMA_VERSION) {
    throw new Error(
      `config schemaVersion ${version} is newer than this build understands (${CONFIG_SCHEMA_VERSION}); upgrade the API`,
    )
  }

  // Only one schema version exists so far; future migrations are chained here
  // before the final parse, each bumping `schemaVersion` as it goes.
  return ServerConfigSchema.parse({ ...document, schemaVersion: CONFIG_SCHEMA_VERSION })
}

export { moduleSchema }
